import React, { useState, Suspense, lazy } from 'react';

const variants = [
    { id: 11, name: 'Neon Gauges', desc: 'Circular gauge meters that fill for each factor' },
    { id: 12, name: 'Stacked Cards', desc: 'Playing cards fan out then stack into result' },
    { id: 13, name: 'Pipeline Flow', desc: 'Values flow through a horizontal pipeline' },
    { id: 14, name: 'Vertical Waterfall', desc: 'Droplets cascade down and merge into total' },
    { id: 15, name: 'LED Scoreboard', desc: 'Retro digital display with neon glow' },
    { id: 16, name: 'Concentric Rings', desc: 'Expanding rings sized to each factor' },
    { id: 17, name: 'Split-Flap Board', desc: 'Airport departure board style flipping digits' },
    { id: 18, name: 'Progress Bars', desc: 'Horizontal bars that converge into total' },
    { id: 19, name: 'Radar Chart', desc: 'Spider chart triangle expanding with values' },
    { id: 20, name: 'Slot Machine', desc: 'Three spinning reels that stop one by one' },
];

// Lazy load all variants
const VariantComponents: Record<number, React.LazyExoticComponent<React.FC>> = {
    11: lazy(() => import('./variants/Variant11').then(m => ({ default: m.HeroGraphic }))),
    12: lazy(() => import('./variants/Variant12').then(m => ({ default: m.HeroGraphic }))),
    13: lazy(() => import('./variants/Variant13').then(m => ({ default: m.HeroGraphic }))),
    14: lazy(() => import('./variants/Variant14').then(m => ({ default: m.HeroGraphic }))),
    15: lazy(() => import('./variants/Variant15').then(m => ({ default: m.HeroGraphic }))),
    16: lazy(() => import('./variants/Variant16').then(m => ({ default: m.HeroGraphic }))),
    17: lazy(() => import('./variants/Variant17').then(m => ({ default: m.HeroGraphic }))),
    18: lazy(() => import('./variants/Variant18').then(m => ({ default: m.HeroGraphic }))),
    19: lazy(() => import('./variants/Variant19').then(m => ({ default: m.HeroGraphic }))),
    20: lazy(() => import('./variants/Variant20').then(m => ({ default: m.HeroGraphic }))),
};

const allIds = variants.map(v => v.id);

export const VariantCompare: React.FC = () => {
    const [selected, setSelected] = useState<number | null>(null);

    if (selected !== null) {
        const Comp = VariantComponents[selected];
        const info = variants.find(v => v.id === selected)!;
        const idx = allIds.indexOf(selected);
        return (
            <div className="min-h-screen bg-slate-950">
                <div className="sticky top-0 z-50 bg-slate-900/90 backdrop-blur border-b border-slate-800 px-4 py-3 flex items-center justify-between">
                    <button
                        onClick={() => setSelected(null)}
                        className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2"
                    >
                        &larr; Back to all variants
                    </button>
                    <span className="text-white font-semibold">
                        Variant {selected}: {info.name}
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setSelected(allIds[Math.max(0, idx - 1)])}
                            disabled={idx === 0}
                            className="px-3 py-1 text-sm rounded bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-30"
                        >
                            Prev
                        </button>
                        <button
                            onClick={() => setSelected(allIds[Math.min(allIds.length - 1, idx + 1)])}
                            disabled={idx === allIds.length - 1}
                            className="px-3 py-1 text-sm rounded bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-30"
                        >
                            Next
                        </button>
                    </div>
                </div>
                <div className="flex items-center justify-center min-h-[calc(100vh-60px)] p-8">
                    <div className="w-full max-w-2xl">
                        <Suspense fallback={<div className="text-slate-500 text-center">Loading...</div>}>
                            <Comp />
                        </Suspense>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950 p-8">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-3xl font-bold text-white mb-2">Payout Formula Variants</h1>
                <p className="text-slate-400 mb-8">10 variants of the Seed &times; Wins &times; Shares formula visualization. Click to view full-size.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {variants.map((v) => {
                        const Comp = VariantComponents[v.id];
                        return (
                            <div
                                key={v.id}
                                onClick={() => setSelected(v.id)}
                                className="cursor-pointer group"
                            >
                                <div className="bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden hover:border-blue-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10">
                                    <div className="h-[350px] overflow-hidden relative">
                                        <Suspense fallback={
                                            <div className="flex items-center justify-center h-full text-slate-600">Loading...</div>
                                        }>
                                            <div className="transform scale-[0.65] origin-top-left w-[154%] h-[154%]">
                                                <Comp />
                                            </div>
                                        </Suspense>
                                    </div>
                                    <div className="p-4 border-t border-slate-800">
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs font-mono text-slate-500 bg-slate-800 px-2 py-1 rounded">
                                                #{v.id}
                                            </span>
                                            <h3 className="text-white font-semibold group-hover:text-blue-400 transition-colors">
                                                {v.name}
                                            </h3>
                                        </div>
                                        <p className="text-sm text-slate-500 mt-1">{v.desc}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
